import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import restaurantService from '../services/restaurantService'
import { Restaurant } from '../types/restaurant'
import MenuList from '../components/MenuList'
import CreateMenuModal from '../components/CreateMenuModal'
import CreateMenuWithAIModal from '../components/CreateMenuWithAIModal'
import RestaurantStaffSection from '../components/RestaurantStaffSection'
import RestaurantTablesSection from '../components/RestaurantTablesSection'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'

const RestaurantDetail: React.FC = () => {
  const { t } = useTranslation()
  const { restaurantId } = useParams<{ restaurantId: string }>()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateMenuModal, setShowCreateMenuModal] = useState(false)
  const [showCreateMenuWithAIModal, setShowCreateMenuWithAIModal] = useState(false)

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!restaurantId) {
        setError(t('error.restaurantIdMissing'))
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const data = await restaurantService.getRestaurant(restaurantId)
        setRestaurant(data)
      } catch (err: any) {
        console.error('Failed to fetch restaurant:', err)
        setError(err.response?.data?.message || t('toast.restaurantLoadFailed'))
        toast.error(t('toast.restaurantLoadFailed'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchRestaurant()
  }, [restaurantId])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('restaurant.notFound.title')}</h2>
          <p className="text-white/80 mb-6">{error || t('restaurant.notFound.defaultMessage')}</p>
          <Link
            to="/dashboard"
            className="inline-block bg-black hover:bg-black/80 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300"
          >
            {t('restaurant.notFound.backToDashboard')}
          </Link>
        </div>
      </div>
    )
  }

  const isOwner = user?._id === restaurant.owner._id
  const isMember = restaurant.members?.some(m => m._id === user?._id) || isOwner

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8 lg:mb-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            {t('common.backToDashboard')}
          </Link>
          <h1 className="text-lg sm:text-2xl lg:text-4xl font-bold text-white mb-2 sm:mb-4">
            {restaurant.name}
          </h1>
        </div>

        {/* Restaurant Info Card */}
        <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-6 sm:p-8 lg:p-10 mb-6 sm:mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left Column - Basic Info */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">{t('restaurant.info.title')}</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-white/70 text-sm font-medium">{t('restaurant.info.typeLabel')}</label>
                  <div className="mt-2">
                    <span className="inline-block px-4 py-2 rounded-full bg-orange-500 text-white font-medium text-sm">
                      {restaurant.restaurantType}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-white/70 text-sm font-medium mb-2 block">{t('restaurant.info.cuisinesLabel')}</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {restaurant.cuisines && restaurant.cuisines.length > 0 ? (
                      restaurant.cuisines.map((cuisine) => (
                        <span
                          key={cuisine.id}
                          className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {cuisine.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-white/60 text-sm">{t('restaurant.info.noCuisines')}</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-white/70 text-sm font-medium">{t('restaurant.info.ownerLabel')}</label>
                  <p className="text-white font-semibold mt-2">{restaurant.owner.name}</p>
                </div>

                <div>
                  <label className="text-white/70 text-sm font-medium">{t('restaurant.info.membersLabel')}</label>
                  <p className="text-white font-semibold mt-2">
                    {t('restaurant.info.membersCount', { count: String((restaurant.members?.length || 0) + 1), label: (restaurant.members?.length || 0) + 1 === 1 ? t('common.member') : t('common.members') })}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Members List */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">{t('restaurant.members.title')}</h2>
              
              <div className="space-y-3">
                {/* Owner */}
                <div className="bg-[#2a2a2a] rounded-xl p-4 flex items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-semibold">
                      {restaurant.owner.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{restaurant.owner.name}</p>
                      <p className="text-white/60 text-xs">{t('restaurant.members.ownerBadge')}</p>
                    </div>
                  </div>
                </div>

                {/* Members */}
                {restaurant.members && restaurant.members.length > 0 ? (
                  restaurant.members.map((member) => (
                    <div key={member._id} className="bg-[#2a2a2a] rounded-xl p-4 flex items-center">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white font-semibold">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-semibold">{member.name}</p>
                          <p className="text-white/60 text-xs">{t('restaurant.members.memberBadge')}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-white/60 text-sm">{t('restaurant.members.noAdditional')}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4">
          {isOwner && (
            <>
              <button
                onClick={() => navigate(`/restaurant/${restaurantId}/edit`)}
                className="px-6 py-3 bg-black border border-white/20 hover:bg-black/80 text-white rounded-lg font-medium transition-all duration-300"
              >
                {t('restaurant.actions.edit')}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(t('restaurant.actions.deleteConfirm'))) return
                  try {
                    await restaurantService.deleteRestaurant(restaurantId!)
                    toast.success(t('toast.restaurantDeleted'))
                    navigate('/dashboard')
                  } catch (err: any) {
                    toast.error(err.response?.data?.message || t('toast.restaurantDeleteFailed'))
                  }
                }}
                className="px-6 py-3 bg-red-900/40 border border-red-500/40 hover:bg-red-900/60 text-red-200 rounded-lg font-medium"
              >
                {t('restaurant.actions.delete')}
              </button>
            </>
          )}
          {!isMember && (
            <button
              onClick={async () => {
                try {
                  await restaurantService.joinRestaurant(restaurantId!)
                  toast.success(t('toast.joinedRestaurant'))
                  // Refresh restaurant data
                  const data = await restaurantService.getRestaurant(restaurantId!)
                  setRestaurant(data)
                } catch (err: any) {
                  toast.error(err.response?.data?.message || t('toast.joinRestaurantFailed'))
                }
              }}
              className="px-6 py-3 bg-black border border-white/20 hover:bg-black/80 text-white rounded-lg font-medium transition-all duration-300"
            >
              {t('restaurant.actions.join')}
            </button>
          )}
          {isMember && !isOwner && (
            <button
              onClick={async () => {
                if (window.confirm(t('common.leaveRestaurantConfirm'))) {
                  try {
                    await restaurantService.leaveRestaurant(restaurantId!)
                    toast.success(t('toast.leftRestaurant'))
                    navigate('/dashboard')
                  } catch (err: any) {
                    toast.error(err.response?.data?.message || t('toast.leaveRestaurantFailed'))
                  }
                }
              }}
              className="px-6 py-3 bg-black border border-white/20 hover:bg-black/80 text-white rounded-lg font-medium transition-all duration-300"
            >
              {t('restaurant.actions.leave')}
            </button>
          )}
        </div>

        {/* Staff & Access */}
        <div className="mt-8 sm:mt-12">
          <RestaurantStaffSection restaurantId={restaurantId!} />
        </div>

        {isOwner && <RestaurantTablesSection restaurantId={restaurantId!} />}

        {/* Orders, Costs, My Ingredients, Create a menu, Create a menu with AI & Menus */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8 sm:mt-12">
          <Link
            to={`/restaurant/${restaurantId}/kds`}
            className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-[180px] transform hover:scale-[1.02] transition-all duration-300 hover:shadow-2xl hover:border-white/20 group"
          >
            <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">👨‍🍳</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('kds.cardTitle')}</h2>
            <p className="text-white/70 text-sm text-center mb-4">{t('kds.cardDescription')}</p>
          </Link>

          <Link
            to={`/restaurant/${restaurantId}/orders`}
            className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-[180px] transform hover:scale-[1.02] transition-all duration-300 hover:shadow-2xl hover:border-white/20 group"
          >
            <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">🧾</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('restaurant.cards.orders.title')}</h2>
            <p className="text-white/70 text-sm text-center mb-4">
              {t('restaurant.cards.orders.description')}
            </p>
            <span className="inline-flex items-center gap-1 text-sky-400 text-sm font-medium">
              {t('restaurant.cards.orders.link')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>

          <Link
            to={`/restaurant/${restaurantId}/costs`}
            className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-[180px] transform hover:scale-[1.02] transition-all duration-300 hover:shadow-2xl hover:border-white/20 group"
          >
            <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">📊</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('restaurant.cards.costs.title')}</h2>
            <p className="text-white/70 text-sm text-center mb-4">
              {t('restaurant.cards.costs.description')}
            </p>
            <span className="inline-flex items-center gap-1 text-sky-400 text-sm font-medium">
              {t('restaurant.cards.costs.link')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>

          <Link
            to={`/restaurant/${restaurantId}/ingredients`}
            className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-[180px] transform hover:scale-[1.02] transition-all duration-300 hover:shadow-2xl hover:border-white/20 group"
          >
            <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">🥗</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('restaurant.cards.ingredients.title')}</h2>
            <p className="text-white/70 text-sm text-center mb-4">
              {t('restaurant.cards.ingredients.description')}
            </p>
            <span className="inline-flex items-center gap-1 text-sky-400 text-sm font-medium">
              {t('restaurant.cards.ingredients.link')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>

          <button
            type="button"
            onClick={() => setShowCreateMenuModal(true)}
            className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-[180px] transform hover:scale-[1.02] transition-all duration-300 hover:shadow-2xl hover:border-white/20 group text-left w-full"
          >
            <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">📝</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('restaurant.cards.createMenu.title')}</h2>
            <p className="text-white/70 text-sm text-center mb-4">
              {t('restaurant.cards.createMenu.description')}
            </p>
            <span className="inline-flex items-center gap-1 text-sky-400 text-sm font-medium">
              {t('restaurant.cards.createMenu.link')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </button>

          <button
            type="button"
            onClick={() => setShowCreateMenuWithAIModal(true)}
            className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-[180px] transform hover:scale-[1.02] transition-all duration-300 hover:shadow-2xl hover:border-white/20 group text-left w-full"
          >
            <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">🖼️</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('restaurant.cards.createMenuAi.title')}</h2>
            <p className="text-white/70 text-sm text-center mb-4">
              {t('restaurant.cards.createMenuAi.description')}
            </p>
            <span className="inline-flex items-center gap-1 text-sky-400 text-sm font-medium">
              {t('restaurant.cards.createMenuAi.link')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </button>

          <div className="md:col-span-2 lg:col-span-4">
            <MenuList
              restaurantId={restaurantId!}
              isOwner={isOwner}
              onMenuCreated={() => setShowCreateMenuModal(false)}
            />
          </div>
        </div>

        {showCreateMenuModal && restaurant && (
          <CreateMenuModal
            restaurantId={restaurantId!}
            restaurant={restaurant}
            onClose={() => setShowCreateMenuModal(false)}
            onSuccess={() => {
              setShowCreateMenuModal(false)
              window.dispatchEvent(new CustomEvent('menu-created'))
            }}
          />
        )}
        {showCreateMenuWithAIModal && restaurantId && (
          <CreateMenuWithAIModal
            restaurantId={restaurantId}
            onClose={() => setShowCreateMenuWithAIModal(false)}
            onSuccess={() => {
              setShowCreateMenuWithAIModal(false)
              window.dispatchEvent(new CustomEvent('menu-created'))
            }}
          />
        )}
      </main>
    </div>
  )
}

export default RestaurantDetail
